/**
 * @module actions/common
 */
import { isEqual } from 'lodash'
import axios from 'axios'
import {
  API_URL,
  SET_BRANDS,
  SET_ENUMS,
  SET_COUNTRIES,
  SET_PHONECODES,
  SET_VEHICLE_LOOKUP_SEVICES,
  SET_HEADERINFO,
  SET_PAGE_TITLE,
  SET_PAGE_TITLE_EXTRA,
  SET_POPUPS,
  SET_HELPICON_POPUP_UPDATES,
  SET_OVERLAYS,
  SET_SLIDEPANELS
} from '../constants'

/**
 * Sets entire helpicon popup stack
 * @param {Number} number
 */
export function setHelpIconPopupUpdates (number) {
  return {
    type: SET_HELPICON_POPUP_UPDATES,
    number
  }
}

/**
 * Sets entire popup stack
 * @param {Array} popups
 */
export function setPopups (popups) {
  return {
    type: SET_POPUPS,
    popups
  }
}

/**
 * Gets entire popup stack
 */
export function getPopups () {
  return (dispatch, getState) => {
    return getState().common.popups
  }
}

/**
 * Checks if a popupId is on the open stack
 * @param {String} popupId
 */
export function isPopupOpen (popupId) {
  return (dispatch) => {
    let popups = dispatch(getPopups())
    for (let i = 0; i < popups.length; i++) {
      const popup = popups[i]
      if (popup.id === popupId) return true
    }
    return false
  }
}

/**
 * Checks if a popupId is on top of the stack
 * @param {String} popupId
 */
export function isPopupOnTop (popupId) {
  return (dispatch) => {
    let popups = dispatch(getPopups())
    return (popups[popups.length - 1].id === popupId)
  }
}

/**
 * Pushes a popupId on to the open stack
 * @param {String} popupId
 */
export function openPopup (popupId) {
  return (dispatch) => {
    if (!dispatch(isPopupOpen(popupId))) {
      let popups = dispatch(getPopups())
      popups.push({id: popupId})
      console.log('Open popup: ', popupId)
      dispatch(setPopups(popups))
    }
  }
}

/**
 * Pops a popupId from the open stack if it is the first in line (ensures popups are handled in the correct order)
 * @param {String} popupId
 * @param {bool} force Forces the popupId to be removed from the stack wherever it may be in the order.
 */
export function closePopup (popupId, force) {
  return (dispatch) => {
    let popups = dispatch(getPopups())
    if (popups.length === 0) return
    for (let i = 0; i < popups.length; i++) {
      const popup = popups[i]
      if (popup.id === popupId && (force || i === popups.length - 1)) {
        popups.splice(i, 1)
        console.log('Close popup: ', popupId)
        dispatch(setPopups(popups))
      }
    }
  }
}

export function closeAllPopups () {
  return (dispatch) => {
    dispatch(setPopups([]))
  }
}

/**
 * Sets entire overlay stack
 * @param {Array} overlays
 */
export function setOverlays (overlays) {
  return {
    type: SET_OVERLAYS,
    overlays
  }
}

/**
 * Gets entire overlay stack
 */
export function getOverlays () {
  return (dispatch, getState) => {
    return getState().common.overlays
  }
}

/**
 * Checks if a overlayId is on the open stack
 * @param {String} overlayId
 */
export function isOverlayOpen (overlayId) {
  return (dispatch) => {
    let overlays = dispatch(getOverlays())
    for (let i = 0; i < overlays.length; i++) {
      const overlay = overlays[i]
      if (overlay.id === overlayId) {
        return true
      }
    }
    return false
  }
}

/**
 * Checks if a overlayId is on top of the stack
 * @param {String} overlayId
 */
export function isOverlayOnTop (overlayId) {
  return (dispatch) => {
    let overlays = dispatch(getOverlays())
    return (overlays[overlays.length - 1].id === overlayId)
  }
}

/**
 * Pushes a overlayId on to the open stack
 * @param {String} overlayId
 */
export function openOverlay (overlayId) {
  return (dispatch) => {
    if (!dispatch(isOverlayOpen(overlayId))) {
      let overlays = dispatch(getOverlays())
      overlays.push({id: overlayId})
      console.log('Open overlay: ', overlayId)
      dispatch(setOverlays(overlays))
    }
  }
}

/**
 * Pops a overlayId from the open stack if it is the first in line (ensures overlays are handled in the correct order)
 * @param {String} overlayId
 * @param {bool} force Forces the overlayId to be removed from the stack wherever it may be in the order.
 */
export function closeOverlay (overlayId, force) {
  return (dispatch) => {
    let overlays = dispatch(getOverlays())
    if (overlays.length === 0) return
    for (let i = 0; i < overlays.length; i++) {
      const overlay = overlays[i]
      if (overlay.id === overlayId && (force || i === overlays.length - 1)) {
        overlays.splice(i, 1)
        console.log('Close overlay: ', overlayId)
        dispatch(setOverlays(overlays))
      }
    }
  }
}

export function closeAllOverlays () {
  return (dispatch) => {
    dispatch(setOverlays([]))
  }
}

/**
 * Sets entire slidePanel stack
 * @param {Array} slidePanels
 */
export function setSlidePanels (slidePanels) {
  return {
    type: SET_SLIDEPANELS,
    slidePanels
  }
}

/**
 * Gets entire slidePanel stack
 */
export function getSlidePanels () {
  return (dispatch, getState) => {
    return getState().common.slidePanels
  }
}

/**
 * Checks if a slidePanelId is on the open stack
 * @param {String} slidePanelId
 */
export function isSlidePanelOpen (slidePanelId) {
  return (dispatch) => {
    let slidePanels = dispatch(getSlidePanels())
    for (let i = 0; i < slidePanels.length; i++) {
      const slidePanel = slidePanels[i]
      if (slidePanel.id === slidePanelId) return true
    }
    return false
  }
}

/**
 * Checks if a slidePanelId is on top of the stack
 * @param {String} slidePanelId
 */
export function isSlidePanelOnTop (slidePanelId) {
  return (dispatch) => {
    let slidePanels = dispatch(getSlidePanels())
    return (slidePanels[slidePanels.length - 1].id === slidePanelId)
  }
}

/**
 * Pushes a slidePanelId on to the open stack
 * @param {String} slidePanelId
 */
export function openSlidePanel (slidePanelId) {
  return (dispatch) => {
    if (!dispatch(isSlidePanelOpen(slidePanelId))) {
      let slidePanels = dispatch(getSlidePanels())
      slidePanels.push({id: slidePanelId})
      console.log('Open slidePanel: ', slidePanelId)
      dispatch(setSlidePanels(slidePanels))
    }
  }
}

/**
 * Pops a slidePanelId from the open stack if it is the first in line (ensures slidePanels are handled in the correct order)
 * @param {String} slidePanelId
 * @param {bool} force Forces the slidePanelId to be removed from the stack wherever it may be in the order.
 */
export function closeSlidePanel (slidePanelId, force) {
  return (dispatch) => {
    let slidePanels = dispatch(getSlidePanels())
    if (slidePanels.length === 0) return
    for (let i = 0; i < slidePanels.length; i++) {
      const slidePanel = slidePanels[i]
      if (slidePanel.id === slidePanelId && (force || i === slidePanels.length - 1)) {
        slidePanels.splice(i, 1)
        console.log('Close slidePanel: ', slidePanelId)
        dispatch(setSlidePanels(slidePanels))
      }
    }
  }
}

export function closeAllSlidePanels () {
  return (dispatch) => {
    dispatch(setSlidePanels([]))
  }
}

/**
 * Sets the page header
 * @param {Object} titleData {title, icon}
 */
export function setPageTitle (titleData) {
  return (dispatch, getState) => {
    if (typeof titleData.title === 'function') {
      titleData = {
        ...titleData,
        title: titleData.title(getState().auth.departmentType)
      }
    }

    if (!isEqual(getState().common.pageTitle)) {
      dispatch({
        type: SET_PAGE_TITLE,
        pageTitle: titleData
      })
    }
  }
}

export function setPageTitleExtra (extra) {
  return (dispatch, getState) => {
    if (getState().common.pageTitle.extra !== extra) {
      dispatch({
        type: SET_PAGE_TITLE_EXTRA,
        pageTitleExtra: extra
      })
    }
  }
}

/**
 * @description Sets header information
 * @return {Promise} Axios promise
 */
export function setHeaderInfo (type, name, data) {
  return dispatch => {
    dispatch({
      type: type,
      [name]: data
    })
  }
}

/**
 * @description Sets the phonecodes
 * @return {Promise} Axios promise
 */
export function setPhoneCodes (phonecodes) {
  return {
    type: SET_PHONECODES,
    phonecodes
  }
}

/**
 * @description Sets the vehicleLookupServices
 * @return {Promise} Axios promise
 */
export function setVehicleLookupServices (vehicleLookupServices) {
  return {
    type: SET_VEHICLE_LOOKUP_SEVICES,
    vehicleLookupServices
  }
}

/**
 * @description Sets the brands
 * @return {Promise} Axios promise
 */
export function setBrands (brands) {
  return {
    type: SET_BRANDS,
    brands
  }
}

/**
 * @description Sets the enums
 * @return {Promise} Axios promise
 */
export function setEnums (enums) {
  return {
    type: SET_ENUMS,
    enums
  }
}

/**
 * @description Sets the countries
 * @return {Promise} Axios promise
 */
export function setCountries (countries) {
  return {
    type: SET_COUNTRIES,
    countries
  }
}

/**
 * @description Getting brands from API and dispatching event
 * @return {Promise} Axios promise
 */
export function getBrands () {
  let url = API_URL + '/Brand'
  return dispatch => {
    return axios.get(url).then((res) => {
      dispatch({
        type: SET_BRANDS,
        brands: res.data
      })
    }, () => {
      dispatch({
        type: SET_BRANDS,
        brands: [{
          Id: 1,
          Name: '2000'
        }]
      })
    })
  }
}

export function getEnums () {
  let url = API_URL + '/Enums/Get'
  return dispatch => {
    return axios.get(url).then((res) => {
      dispatch({
        type: SET_ENUMS,
        enums: res.data
      })
      return res
    })
  }
}

export function getHeaderInfo (id) {
  let url = API_URL + '/Users/GetHeaderInfo/' + id
  return dispatch => {
    return axios.get(url).then((res) => {
      dispatch({
        type: SET_HEADERINFO,
        headerInfo: res.data
      })
      return res
    })
  }
}

export function getCustomerLanguage (customerId) {
  let url = API_URL + '/Resources/Language/Customer/Get/' + customerId
  return dispatch => {
    return axios.get(url)
  }
}

export function getShopLanguage (shopId) {
  let url = API_URL + '/Resources/Language/Shop/Get/' + shopId
  return dispatch => {
    return axios.get(url)
  }
}

export function getSubscriptions () {
  let url = API_URL + '/Resources/Subscriptions/Get'
  return dispatch => {
    return axios.get(url)
  }
}

/**
 * @description Getting supportlink from api
 * @return {Promise} Axios promise
 */
export function getSupportLink () {
  let url = API_URL + '/Resources/CreateSupportLink/'
  return dispatch => {
    return axios.get(url)
  }
}



// WEBPACK FOOTER //
// ./src/app/actions/common.js